import { useMemo } from 'react';
import type { TemplateCategory } from '../hooks/useDocumentTemplates';
import {
  getCategoryConfig,
  buildTotalsHtml,
  buildBillingEntityHtml,
  buildConditionHtml,
} from '../lib/categoryBlocks';

interface EditorToolbarProps {
  category: TemplateCategory;
  onInsertBlock: (html: string) => void;
}

interface ToolbarBlock {
  label: string;
  icon: string;
  description: string;
  html: string;
}

/**
 * Builds the code-mode insert blocks for a category. The Totals and Condition
 * blocks use the entity variables that match the selected document type (e.g.
 * `quote.total` instead of `invoice.total`); the Totals block is dropped for
 * categories that have no monetary totals (e.g. Report, Letter).
 */
function buildToolbarBlocks(category: TemplateCategory): ToolbarBlock[] {
  const cfg = getCategoryConfig(category);

  const blocks: ToolbarBlock[] = [
    {
      label: 'Table',
      icon: '⊞',
      description: 'Insert a data table',
      html: `<table style="width:100%;border-collapse:collapse;margin:16px 0;">
  <thead>
    <tr style="background:#f9fafb;">
      <th style="padding:8px 12px;text-align:left;font-size:11px;font-weight:600;border-bottom:2px solid #e5e7eb;">Column 1</th>
      <th style="padding:8px 12px;text-align:left;font-size:11px;font-weight:600;border-bottom:2px solid #e5e7eb;">Column 2</th>
      <th style="padding:8px 12px;text-align:right;font-size:11px;font-weight:600;border-bottom:2px solid #e5e7eb;">Amount</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f3f4f6;">Data</td>
      <td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f3f4f6;">Data</td>
      <td style="padding:8px 12px;font-size:12px;text-align:right;border-bottom:1px solid #f3f4f6;">$0.00</td>
    </tr>
  </tbody>
</table>`,
    },
  ];

  if (cfg.hasLineItems) {
    blocks.push({
      label: 'Loop',
      icon: '↻',
      description: 'Repeat a block for each item in an array',
      html: `{{#each line_items}}
<div style="padding:6px 0;border-bottom:1px solid #eee;">
  <span>{{this.description}}</span> — <span>{{currency this.line_total}}</span>
</div>
{{/each}}`,
    });
  }

  const totalsHtml = buildTotalsHtml(cfg);
  if (totalsHtml) {
    blocks.push({
      label: 'Totals',
      icon: 'Σ',
      description: 'Subtotal / Tax / Total block',
      html: totalsHtml,
    });
  }

  const billingEntityHtml = buildBillingEntityHtml(cfg);
  if (billingEntityHtml) {
    blocks.push({
      label: 'Billing Entity',
      icon: '🏦',
      description: "Attached practice entity's billing details",
      html: billingEntityHtml,
    });
  }

  blocks.push(
    {
      label: 'Address',
      icon: '⌂',
      description: 'Client address block',
      html: `<div style="margin:12px 0;">
  <h4 style="font-size:11px;color:#6b7280;text-transform:uppercase;margin:0 0 4px;">Bill To</h4>
  <p style="margin:0;font-weight:600;">{{client.business_name}}</p>
  <p style="margin:2px 0;">{{client.name}}</p>
  <p style="margin:2px 0;color:#6b7280;">{{client.address}}</p>
</div>`,
    },
    {
      label: 'Heading',
      icon: 'H',
      description: 'Section heading',
      html: `<h2 style="font-size:16px;font-weight:600;margin:20px 0 8px;color:#1f2937;">Section Title</h2>`,
    },
    {
      label: 'Divider',
      icon: '—',
      description: 'Horizontal line separator',
      html: `<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />`,
    },
    {
      label: 'Condition',
      icon: '?',
      description: 'Show/hide content based on a condition',
      html: buildConditionHtml(cfg),
    },
    {
      label: 'Logo',
      icon: '☆',
      description: 'Firm logo image',
      html: `{{#if firm.logo_url}}<img src="{{firm.logo_url}}" style="height:50px;margin-bottom:8px;" alt="{{firm.name}}" />{{/if}}`,
    },
    {
      label: '2-Col',
      icon: '⊟',
      description: 'Two column layout',
      html: `<div style="display:flex;gap:24px;margin:16px 0;">
  <div style="flex:1;">
    <!-- Left column -->
    <p>Left content</p>
  </div>
  <div style="flex:1;">
    <!-- Right column -->
    <p>Right content</p>
  </div>
</div>`,
    },
  );

  return blocks;
}

/**
 * Toolbar with clickable blocks that insert pre-built HTML snippets into the editor.
 * Helps users who aren't comfortable writing raw HTML/Handlebars from scratch.
 */
export function EditorToolbar({ category, onInsertBlock }: EditorToolbarProps) {
  const blocks = useMemo(() => buildToolbarBlocks(category), [category]);

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800">
      <span className="mr-2 text-xs font-medium text-gray-500 dark:text-gray-400">Insert:</span>
      {blocks.map((block) => (
        <button
          key={block.label}
          onClick={() => onInsertBlock(block.html)}
          title={block.description}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-indigo-100 hover:text-indigo-700 dark:text-gray-300 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400"
        >
          <span className="text-sm">{block.icon}</span>
          {block.label}
        </button>
      ))}
    </div>
  );
}
